import { HARDWARE_CHECKOUT_ALLOWED_COUNTRIES } from "@screencloud/billing-client-api";
import { FormattedMessage } from "react-intl";
import { Constants } from "src/billinglatest/constants";
import { formatted } from "src/billinglatest/util/currency";
import { countryName } from "../../countryName";
import {
  OTHER_SHIPPING_COUNTRY,
  isOtherShippingCountry,
} from "../../shippingCountries";
import pixiDevice from "../NumberOfScreensStep/assets/pixi-device.svg";
import {
  BillingCycle,
  CurrencyCode,
  DealId,
  DISCOUNT_PERCENT,
  PER_DEVICE_VALUE_CENTS,
  dealEligibility,
} from "./constants";
import {
  ChevronIcon,
  CountrySelect,
  DealCardStyled,
  DealContent,
  DealFootnote,
  DealPriceRow,
  DealSavings,
  DealTitle,
  DealsColumn,
  DealsEmpty,
  DealsHeader,
  DealsList,
  DealsSubtitle,
  DealsTitle,
  DeviceCount,
  DeviceImage,
  DiscountPercent,
  DiscountValue,
  DiscountWord,
  DropdownChip,
  FootnoteGroup,
  GlobeIcon,
  SaveAmount,
  SelectValueGroup,
  SelectValueText,
  ShipToLabel,
  ShipToRow,
  UnavailableDeal,
} from "./styles";

export interface ExclusiveDealsProps {
  billingCycle: BillingCycle;
  currency: CurrencyCode;
  licenses: number;
  selectedDealId?: DealId;
  shippingCountry: string;
  /** Translated label for the FE-only unsupported-location "Ship to" option. */
  shipOtherLabel: string;
  onSelectDeal: (dealId: DealId) => void;
  onChangeShippingCountry: (value: string) => void;
}

export const ExclusiveDeals = ({
  billingCycle,
  currency,
  licenses,
  selectedDealId,
  shippingCountry,
  shipOtherLabel,
  onSelectDeal,
  onChangeShippingCountry,
}: ExclusiveDealsProps) => {
  const eligibility = dealEligibility(billingCycle);
  const isOther = isOtherShippingCountry(shippingCountry);
  const savings = formatted(
    currency,
    PER_DEVICE_VALUE_CENTS[currency] * licenses,
  );

  const deviceSavings = (
    <DealSavings>
      <SaveAmount>
        <FormattedMessage
          id="pixi_wizard.plan.deals.save"
          defaultMessage="Save {amount}"
          values={{ amount: savings }}
        />
      </SaveAmount>
      <DeviceCount>
        <FormattedMessage
          id="pixi_wizard.plan.deals.devices"
          defaultMessage="x{count} {count, plural, one {Device} other {Devices}}"
          values={{ count: licenses }}
        />
      </DeviceCount>
    </DealSavings>
  );

  // The visible label mirrors the selected option's text; the native <select>
  // beneath is transparent, so this drives the chip's (hugging) width.
  const selectedCountryLabel = isOther
    ? shipOtherLabel
    : countryName(shippingCountry);

  // The "Ship to" selector, shown in both the available and unavailable
  // free-device states. `Other` is an FE-only option (not in the hardware
  // allow-list) that flips the deal to its unavailable state (GRWBKK-1730).
  const shipToSelector = (
    <ShipToRow>
      <ShipToLabel>
        <FormattedMessage
          id="pixi_wizard.plan.deals.ship_to"
          defaultMessage="Ship to"
        />
      </ShipToLabel>
      <DropdownChip $selected={isOther}>
        <GlobeIcon name="globe" aria-hidden />
        <SelectValueGroup aria-hidden>
          <SelectValueText>{selectedCountryLabel}</SelectValueText>
          <ChevronIcon name="arrow-down" />
        </SelectValueGroup>
        <CountrySelect
          data-testid="shipping-country-select"
          aria-label="Shipping country"
          value={shippingCountry}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => onChangeShippingCountry(event.target.value)}
        >
          {HARDWARE_CHECKOUT_ALLOWED_COUNTRIES.map((code) => (
            <option key={code} value={code}>
              {Constants.Countries[code]}
            </option>
          ))}
          <option value={OTHER_SHIPPING_COUNTRY}>{shipOtherLabel}</option>
        </CountrySelect>
      </DropdownChip>
    </ShipToRow>
  );

  return (
    <DealsColumn data-testid="exclusive-deals">
      <DealsHeader>
        <DealsTitle>
          <FormattedMessage
            id="pixi_wizard.plan.deals.title"
            defaultMessage="Exclusive deals"
          />
        </DealsTitle>
        <DealsSubtitle>
          <FormattedMessage
            id="pixi_wizard.plan.deals.subtitle"
            defaultMessage="Depending on the plan you choose, you'll get access to exclusive one-time deals."
          />
        </DealsSubtitle>
      </DealsHeader>

      {!eligibility.anyDeal ? (
        <DealsEmpty data-testid="deals-empty">
          <FormattedMessage
            id="pixi_wizard.plan.deals.none"
            defaultMessage="Unfortunately, there are no deals available on this plan"
          />
        </DealsEmpty>
      ) : (
        <DealsList>
          {eligibility.freeDeviceAvailable &&
            (isOther ? (
              <UnavailableDeal data-testid="deal-free-device-unavailable">
                <DealContent>
                  <DealTitle>
                    <FormattedMessage
                      id="pixi_wizard.plan.deals.unavailable"
                      defaultMessage="This offer is unavailable"
                    />
                  </DealTitle>
                  <DealPriceRow>
                    <DeviceImage src={pixiDevice} alt="" aria-hidden />
                    {deviceSavings}
                  </DealPriceRow>
                  <FootnoteGroup>
                    <DealFootnote>
                      <FormattedMessage
                        id="pixi_wizard.plan.deals.cannot_ship"
                        defaultMessage="We cannot ship to this location"
                      />
                    </DealFootnote>
                    {shipToSelector}
                  </FootnoteGroup>
                </DealContent>
              </UnavailableDeal>
            ) : (
              <DealCardStyled
                selected={selectedDealId === "free-device"}
                onClick={() => onSelectDeal("free-device")}
                data-testid="deal-free-device"
              >
                <DealContent>
                  <DealTitle>
                    <FormattedMessage
                      id="pixi_wizard.plan.deals.free_devices"
                      defaultMessage="I want free devices!"
                    />
                  </DealTitle>
                  <DealPriceRow>
                    <DeviceImage src={pixiDevice} alt="" aria-hidden />
                    {deviceSavings}
                  </DealPriceRow>
                  <FootnoteGroup>
                    <DealFootnote>
                      <FormattedMessage
                        id="pixi_wizard.plan.deals.per_license"
                        defaultMessage="1 free device for every license"
                      />
                    </DealFootnote>
                    {shipToSelector}
                  </FootnoteGroup>
                </DealContent>
              </DealCardStyled>
            ))}

          {eligibility.discountAvailable && (
            <DealCardStyled
              selected={selectedDealId === "discount"}
              onClick={() => onSelectDeal("discount")}
              data-testid="deal-discount"
            >
              <DealContent>
                <DealTitle>
                  <FormattedMessage
                    id="pixi_wizard.plan.deals.discount"
                    defaultMessage="I want a discount"
                  />
                </DealTitle>
                <DiscountValue>
                  <DiscountPercent data-testid="discount-percent">
                    {DISCOUNT_PERCENT}%
                  </DiscountPercent>{" "}
                  <DiscountWord>
                    <FormattedMessage
                      id="pixi_wizard.plan.deals.discount_word"
                      defaultMessage="Discount"
                    />
                  </DiscountWord>
                </DiscountValue>
              </DealContent>
            </DealCardStyled>
          )}
        </DealsList>
      )}
    </DealsColumn>
  );
};
